import React from 'react'
import Button from '@mui/material/Button'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'

import { DELETEResponse } from '@cbgms/api'

import { useDeleteMachine } from './use-delete-machine'
import { ConfirmationDialog } from './ConfirmationDialog'
import type { Option } from './DeleteReasonDropdown'
import { RejectionDialog } from './RejectionDialog'
import { WarningDialog } from './WarningDialog'

export interface DeleteButtonProps {
  /**
   * A component you can provide to render a different button.
   */
  Button?: React.ComponentType<ButtonProps>
  /**
   * The translation replacement string for the item that's being removed.
   */
  deletedItemString: string
  /**
   * The translation scope so we can show the user
   * specific rejections and warnings.
   */
  scope: string
  /**
   * These options are rendered in the confirmation and warning
   * dialog when provided. When reasons are provided selecting
   * a reason is required before you can delete the item.
   */
  deleteReasons?: Option[]
  handleDelete: (checkIfDeleteIsPossible: boolean, deleteReason?: string) => Promise<DELETEResponse>
  handleDeleteSuccess: () => Promise<void>
  /**
   * `reasonIsRequired` is a flag you can use to require a reason to be given before
   * being able to complete the delete.
   * @default false
   */
  reasonIsRequired?: boolean
}

export function DeleteButton({
  deletedItemString,
  scope,
  deleteReasons,
  handleDelete,
  handleDeleteSuccess,
  Button = DefaultButton,
  reasonIsRequired = false
}: DeleteButtonProps) {
  const [state, send] = useDeleteMachine({ handleDelete, handleDeleteSuccess })

  const handleClick = () => {
    send('START')
  }

  const handleCancel = () => {
    send('CANCEL')
  }

  const handleConfirm = (deleteReason?: string) => {
    send({ type: 'CONFIRM', deleteReason })
  }

  return (
    <>
      {state.matches('containsRejections') && (
        <RejectionDialog
          deletedItemString={deletedItemString}
          scope={scope}
          onCancel={handleCancel}
          rejections={state.context.rejections}
          warnings={state.context.warnings}
        />
      )}
      {state.matches('containsWarnings') && (
        <WarningDialog
          deletedItemString={deletedItemString}
          scope={scope}
          deleteReasons={deleteReasons}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          rejections={state.context.rejections}
          warnings={state.context.warnings}
          reasonIsRequired={reasonIsRequired}
        />
      )}
      {state.matches('requiresConfirmation') && (
        <ConfirmationDialog
          deletedItemString={deletedItemString}
          deleteReasons={deleteReasons}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          reasonIsRequired={reasonIsRequired}
        />
      )}
      <Button onClick={handleClick} />
    </>
  )
}

/**
 * nonRejectionResult is for use cases where we dont have a dryrun.
 * Return this from your function onDelete when called when dryrun true.
 */
export const nonRejectionResult: DELETEResponse = { Data: { Rejections: [], Warnings: [] } }

interface ButtonProps {
  onClick: () => void
  autoFocus?: boolean
}

function DefaultButton(props: ButtonProps) {
  const { t } = useTranslation()

  return (
    <Button onClick={props.onClick} autoFocus={props.autoFocus} startIcon={<DeleteIcon />} variant='outlined'>
      {t('DeleteFlow.Button')}
    </Button>
  )
}
